import { useTranslation } from 'react-i18next';

import { isAppError } from '@cenorion/api';
import { Button } from '@cenorion/ui/components/button';

export type ErrorFallbackVariant = 'root' | 'route' | 'widget';

const CONTAINER_CLASS: Record<ErrorFallbackVariant, string> = {
  root: 'flex min-h-svh flex-col items-center justify-center gap-3 p-6 text-center',
  route:
    'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center',
  widget:
    'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed p-4 text-center text-sm',
};

/** Maps the lowercase variant to the PascalCase i18n key segment. */
const VARIANT_KEY: Record<ErrorFallbackVariant, 'Root' | 'Route' | 'Widget'> = {
  root: 'Root',
  route: 'Route',
  widget: 'Widget',
};

type ErrorFallbackProps = {
  error: unknown;
  variant: ErrorFallbackVariant;
  onRetry: () => void;
};

/**
 * Shared fallback UI for all three boundary levels: never shows raw `error.message` (an AxiosError
 * message can carry a URL or a status line) or a stack trace, even in development, so a boundary
 * looks the same in dev and production. Only `traceId` is ever rendered, so a user can hand it to
 * support without exposing anything else.
 */
export function ErrorFallback({ error, variant, onRetry }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const traceId = isAppError(error) ? error.traceId : undefined;

  return (
    <div className={CONTAINER_CLASS[variant]} role="alert">
      <h2 className={variant === 'root' ? 'text-lg font-semibold' : 'font-semibold'}>
        {t(`Error.Boundary.${VARIANT_KEY[variant]}.Title`)}
      </h2>
      <p className="text-muted-foreground">
        {t(`Error.Boundary.${VARIANT_KEY[variant]}.Description`)}
      </p>
      {traceId && (
        <p className="text-xs text-muted-foreground">{t('Error.Boundary.TraceId', { traceId })}</p>
      )}
      <Button variant="outline" size={variant === 'widget' ? 'sm' : 'default'} onClick={onRetry}>
        {t(variant === 'root' ? 'Error.Boundary.Reload' : 'Error.Retry')}
      </Button>
    </div>
  );
}
